import { PayRange } from './offer/type'

/**
 * Formats a contract type string into a standardized French format.
 * Uses case-insensitive regex matching to identify contract types.
 * 
 * @param contractType - The contract type string to format (can be null)
 * @returns Formatted contract type in French or "Non précisé" if not recognized
 * 
 * @example
 * formatContractType('CDI') // returns 'CDI'
 * formatContractType('cdi') // returns 'CDI'
 * formatContractType('stage') // returns 'Stage'
 * formatContractType('internship') // returns 'Stage'
 * formatContractType('freelance') // returns 'Indépendant'
 * formatContractType(null) // returns 'Non précisé'
 * formatContractType('Unknown') // returns 'Non précisé'
 */
export function formatContractType(contractType: string | null): string {
  if (!contractType) {
    return 'Non précisé'
  }
  switch (true) {
    case /cdi/i.test(contractType):
      return 'CDI'

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    case /stage/i.test(contractType!):
    case /internship/i.test(contractType):
      return 'Stage'

    case /cdd/i.test(contractType):
      return 'CDD'

    case /alternance/i.test(contractType):
      return 'Alternance'

    case /freelance/i.test(contractType):
      return 'Indépendant'

    default:
      return 'Non précisé'
  }
}

/**
 * Formats a start date string into a user-friendly French format.
 * Preserves special strings like "Dès que possible" and "Début" prefixed dates.
 * 
 * @param startDate - The start date string to format (can be null)
 * @returns Formatted start date in French or "Dès que possible" as default
 * 
 * @example
 * formatStartDate('2024-01-15') // returns 'Début le 15/01/2024'
 * formatStartDate('Dès que possible') // returns 'Dès que possible'
 * formatStartDate('Début janvier') // returns 'Début janvier'
 * formatStartDate(null) // returns 'Dès que possible'
 * formatStartDate('') // returns 'Dès que possible'
 */
export function formatStartDate(startDate: string | null): string {
  if (startDate && typeof startDate === 'string') {
    if (startDate === 'Dès que possible' || startDate.startsWith('Début')) {
      return startDate
    }
    return `Début le ${new Date(startDate).toLocaleDateString('fr-FR')}`
  }
  return 'Dès que possible'
}

/**
 * Formats a salary range into a user-friendly French format.
 * Returns different formats based on available salary information.
 * 
 * @param payRange - Object containing min and max salary values
 * @param payRange.min - Minimum salary in thousands of euros (can be null)
 * @param payRange.max - Maximum salary in thousands of euros (can be null)
 * @returns Formatted salary string or null if no valid salary data
 * 
 * @example
 * formatSalary({ min: 30, max: 50 }) // returns '30 à 50 K€'
 * formatSalary({ min: 40, max: null }) // returns 'À partir de 40 K€'
 * formatSalary({ min: 0, max: 50 }) // returns null
 * formatSalary({ min: null, max: null }) // returns null
 * formatSalary({ min: 40, max: 40 }) // returns 'À partir de 40 K€'
 */
export function formatSalary({ min, max }: PayRange) {
  if (min && max && min !== max && min !== 0 && max !== 0) {
    return `${min.toString()} à ${max.toString()} K€`
  }
  if (min && min !== 0) {
    return `À partir de ${min.toString()} K€`
  }
  return null
}

/**
 * Converts a salary range from thousands to actual euros for Google Jobs format.
 * Multiplies the input by 1000 to convert from K€ to €.
 * 
 * @param range - Salary range in thousands of euros (can be null or undefined)
 * @returns Salary range in euros or null if input is invalid
 * 
 * @example
 * formatRangeForGoogleJobs(30) // returns 30000
 * formatRangeForGoogleJobs(50) // returns 50000
 * formatRangeForGoogleJobs(null) // returns null
 * formatRangeForGoogleJobs(undefined) // returns null
 * formatRangeForGoogleJobs(0) // returns null
 */
export function formatRangeForGoogleJobs(
  range: number | null | undefined,
): number | null {
  if (range && range !== 0) {
    return range * 1000
  }
  return null
}

/**
 * Formats an experience level into a user-friendly French description.
 * Supports both numeric levels (0-4) and string-based levels from Lamacompta system.
 * 
 * @param level - Experience level as number or string
 * @returns Formatted experience description in French
 * 
 * @example
 * formatExperienceLevel(0) // returns 'Non précisé'
 * formatExperienceLevel(1) // returns "- 1 an d'expérience"
 * formatExperienceLevel('Lamajunior (- 1 an)') // returns "- 1 an d'expérience"
 * formatExperienceLevel(4) // returns "+ 5 ans d'expérience"
 * formatExperienceLevel('Lamexpert (+ 5 ans)') // returns "+ 5 ans d'expérience"
 * formatExperienceLevel('Unknown') // returns 'Non précisé'
 */
export function formatExperienceLevel(level: number | string): string {
  switch (level) {
    case 0:
      return 'Non précisé'
    case 1:
    case 'Lamajunior (- 1 an)':
      return "- 1 an d'expérience"
    case 2:
    case 'Lamaffirmé (1 à 2 ans)':
      return "1 à 2 ans d'expérience"
    case 3:
    case 'Lamasenior (2 à 5 ans)':
      return "2 à 5 ans d'expérience"
    case 4:
    case 'Lamexpert (+ 5 ans)':
      return "+ 5 ans d'expérience"
    default:
      return 'Non précisé'
  }
}

/**
 * Finds the most recent date from an array of date strings.
 * Converts all dates to timestamps and returns the latest one as a Date object.
 * 
 * @param dates - Array of date strings to compare
 * @returns The most recent date as a Date object, or null if array is empty
 * 
 * @example
 * getLastUpdateDate(['2024-01-01', '2024-01-15', '2024-01-10']) // returns Date('2024-01-15')
 * getLastUpdateDate(['2024-01-15']) // returns Date('2024-01-15')
 * getLastUpdateDate([]) // returns null
 * getLastUpdateDate(null) // returns null
 */
export function getLastUpdateDate(dates: string[]): Date | null {
  if (!dates || dates.length === 0) {
    return null
  }
  return new Date(Math.max(...dates.map((d) => new Date(d).getTime())))
}

/**
 * Formats a published date into a user-friendly French string.
 * Handles both Date objects and date strings, formatting them in French locale.
 * 
 * @param publishedDate - The published date as Date object or string
 * @returns Formatted date string in French or empty string if invalid
 * 
 * @example
 * formatPublishedDate(new Date('2024-01-15')) // returns 'Offre publiée le 15/01/2024'
 * formatPublishedDate('2024-01-15') // returns 'Offre publiée le 15/01/2024'
 * formatPublishedDate('2024-01-15T10:00:00Z') // returns 'Offre publiée le 15/01/2024'
 * formatPublishedDate(null) // returns ''
 * formatPublishedDate('invalid-date') // returns 'Offre publiée le Invalid Date'
 */
export function formatPublishedDate(publishedDate: Date | string): string {
  if (!publishedDate) {
    return ''
  }
  if (publishedDate && publishedDate instanceof Date) {
    return `Offre publiée le ${publishedDate.toLocaleDateString('fr-FR')}`
  }
  if (publishedDate && typeof publishedDate === 'string') {
    return `Offre publiée le ${new Date(publishedDate).toLocaleDateString('fr-FR')}`
  }
  return ''
}

/**
 * Gets the most recent date from offer lifecycle dates (published, updated, created).
 * Filters out null values and returns the latest date using {@link getLastUpdateDate}.
 * 
 * @param params - Object containing offer lifecycle dates
 * @param params.publishedAt - Publication date (can be null)
 * @param params.updatedAt - Last update date (can be null)
 * @param params.createdAt - Creation date (can be null)
 * @returns The most recent date as a Date object, or null if no valid dates
 * 
 * @example
 * getLastUpdateDateFormatted({
 *   publishedAt: '2024-01-01',
 *   updatedAt: '2024-01-15',
 *   createdAt: '2024-01-10'
 * }) // returns Date('2024-01-15')
 * 
 * getLastUpdateDateFormatted({
 *   publishedAt: null,
 *   updatedAt: '2024-01-10',
 *   createdAt: null
 * }) // returns Date('2024-01-10')
 * 
 * getLastUpdateDateFormatted({
 *   publishedAt: null,
 *   updatedAt: null,
 *   createdAt: null
 * }) // returns null
 */
export function getLastUpdateDateFormatted({
  publishedAt,
  updatedAt,
  createdAt,
}: {
  createdAt: string | null
  publishedAt: string | null
  updatedAt: string | null
}): Date | null {
  const dates = [publishedAt, updatedAt, createdAt].filter((date): date is string => Boolean(date))
  return getLastUpdateDate(dates)
}
